import { Component, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute } from '@angular/router';
import { Filter } from '@app/shared/models/filter';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { DialogGuestsForListingComponent } from '../../components/dialog-guests-for-listing/dialog-guests-for-listing.component';
import {
  ActivePropertiesService,
  ActivePropertiesTotals,
  BillingCycle,
  PropertiesPagination,
  Property,
} from '../../services/active-properties/active-properties.service';

const UNMUTED_FILTER: Filter = {
  id: 'muted',
  label: 'Muted',
  type: 'list',
  inverted: false,
  lockable: false,
  valuesData: [
    { key: 'all', label: 'All' },
    {
      key: 'yes',
      label: 'Muted',
    },
    {
      key: 'no',
      label: 'Unmuted',
    },
  ],
  selectedValue: {
    key: 'no',
    label: 'Unmuted',
  },
};

@Component({
  standalone: false,
  selector: 'sbnb-active-properties',
  templateUrl: './active-properties.component.html',
  styleUrls: ['./active-properties.component.scss'],
})
export class ActivePropertiesComponent implements OnInit {
  public pricingBillingFaqLink = 'https://my.hospitable.com/support-documentation/category/2574375-subscription';
  loading: boolean;
  loadingProperties: boolean;
  billingCycles: BillingCycle[];
  properties: Property[];
  totals: ActivePropertiesTotals;
  paginationData: PropertiesPagination;
  searchCriteria = '';
  currentPageNumber = 1;
  currentFilters: Filter[] = [UNMUTED_FILTER];
  public currentCycle$ = new BehaviorSubject<BillingCycle>(null);
  public currentCycleData$ = this.currentCycle$.pipe(
    filter((cycle) => !!cycle),
    switchMap((cycle) =>
      this.activePropertiesService.fetchActivePropertyCycleSummary(cycle.key).pipe(
        map((cycleSummary) => {
          return {
            cycle,
            cycleSummary: cycleSummary?.data,
          };
        })
      )
    )
  );
  // Invoices can link to this page with the matching billing cycle selected
  public invoiceId$ = this.route.queryParams.pipe(map((params) => params.invoice_id ?? null));

  constructor(
    private activePropertiesService: ActivePropertiesService,
    private dialog: MatDialog,
    private readonly route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.loading = true;
    this.fetchBillingCycles();
  }

  fetchBillingCycles() {
    combineLatest([this.activePropertiesService.fetchBillingCycles(), this.invoiceId$]).subscribe(
      ([cycles, invoiceId]) => {
        this.billingCycles = cycles;

        // We don't have any billing cycles
        if (!this.billingCycles.length) {
          // todo
          return;
        }

        const cycle = this.billingCycles.find((c) => c.invoice_id === invoiceId) ?? this.billingCycles[0];
        this.currentCycle$.next(cycle);

        this.fetchProperties();
      }
    );
  }

  fetchProperties() {
    const timestamp = this.currentCycle$.value?.key;
    this.loadingProperties = true;

    this.activePropertiesService
      .fetchProperties(timestamp, this.searchCriteria, this.currentPageNumber, this.currentFilters)
      .subscribe((res) => {
        this.properties = res.data;
        this.totals = res.meta.totals;
        this.paginationData = res.meta.pagination;
        this.loading = false;
        this.loadingProperties = false;
      });
  }

  handlePageChanged(newPageNo: number) {
    this.currentPageNumber = newPageNo;
    this.fetchProperties();
  }

  searchUpdated(criteria: string) {
    this.searchCriteria = criteria;
    this.currentPageNumber = 1;
    this.fetchProperties();
  }

  filtersUpdated(filters: Filter[]) {
    this.currentFilters = filters;
    this.currentPageNumber = 1;
    this.fetchProperties();
  }

  cycleChanged(cycle: BillingCycle) {
    this.currentCycle$.next(cycle);
    this.searchCriteria = null;
    this.currentFilters = [UNMUTED_FILTER];
    this.currentPageNumber = 1;
    this.fetchProperties();
  }

  clearFilters() {
    this.currentFilters = [UNMUTED_FILTER];
    this.searchCriteria = '';
    this.fetchProperties();
  }

  openListingDialog(property: Property) {
    const dialogRef = this.dialog.open(DialogGuestsForListingComponent, {
      maxHeight: '90vh',
      maxWidth: '487px',
      width: '90vw',
      data: {
        guests: property.guest_list,
      },
    });
  }
}
